import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchWithAuth } from "../../../auth/api";
import {
  HiCollection,
  HiInformationCircle,
  HiPhotograph,
  HiUpload,
  HiArrowLeft,
  HiPlusCircle,
  HiArrowRight,
  HiSelector,
} from "react-icons/hi";
import { compressImage, dataURLtoFile } from "../../../utils/imageCompressor";

function CreateService() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isCategoryMode = searchParams.get("mode") === "category";

  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    service_image: null,
    name: "",
    description: "",
    parent_id: "",
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetchWithAuth("get", "servicesWithNullParent");
        setCategories(response.data || []);
      } catch (error) {
        toast.error("Failed to load existing categories");
      }
    };
    if (!isCategoryMode) {
      fetchCategories();
    }
  }, [isCategoryMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    if (selectedFiles.length + files.length > 5) {
      toast.warning("You can attach up to 5 images.");
    }

    try {
      const remainingSlots = 5 - selectedFiles.length;
      const filesToProcess = files.slice(0, remainingSlots);

      const processedFiles = await Promise.all(
        filesToProcess.map(async (file) => {
          const dataUrl = await compressImage(file, 300 * 1024);
          if (dataUrl) {
            return dataURLtoFile(dataUrl, file.name);
          }
          return file;
        })
      );

      const validFiles = processedFiles.filter(Boolean);
      setSelectedFiles((prev) => [...prev, ...validFiles]);
      setPreviews((prev) => [
        ...prev,
        ...validFiles.map((file) => URL.createObjectURL(file)),
      ]);
    } catch (err) {
      console.error("Compression error:", err);
    }
  };

  const removeImage = (index) => {
    setSelectedFiles((prev) => prev.filter((_, idx) => idx !== index));
    setPreviews((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isCategoryMode && !formData.parent_id) {
      toast.warning("Please select a parent category for this service.");
      return;
    }

    if (!isCategoryMode && selectedFiles.length === 0) {
      toast.error("Please provide at least one service image");
      return;
    }

    setIsLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("parent_id", isCategoryMode ? "" : formData.parent_id);

      selectedFiles.forEach((file) => {
        formDataToSend.append("images", file);
      });

      const response = await fetchWithAuth(
        "POST",
        "createService",
        formDataToSend,
      );

      if (response.ok || response.status === 201) {
        toast.success(isCategoryMode ? "Category created successfully!" : "Service created successfully!");
        const targetPath = isCategoryMode
          ? "/admin/service-categories"
          : "/admin/services";
        navigate(targetPath);
      } else {
        const errorMsg = await response.text();
        toast.error(errorMsg || "Failed to create service");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-white rounded-xl text-slate-400 hover:text-blue-600 transition-all shadow-sm"
        >
          <HiArrowLeft size={20} />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
            {isCategoryMode ? "Create New Category" : "Create New Service"}
          </h2>
          <p className="text-slate-500 text-sm font-medium">
            {isCategoryMode
              ? "Add a fresh top-level service category to your catalog."
              : "Add a new service offering under an existing category."}
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-[2.5rem] p-8 md:p-10 border border-slate-200/60 shadow-xl shadow-slate-100/50 space-y-8"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 flex items-center gap-2 ml-1">
                <HiPlusCircle className="text-blue-500" />{" "}
                {isCategoryMode ? "Category Name" : "Service Name"}
              </label>
              <input
                type="text"
                name="name"
                placeholder={isCategoryMode ? "e.g. Waterproofing Treatment" : "e.g. Membrane Sheet Installation"}
                value={formData.name}
                onChange={handleChange}
                required
                className="input-field"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 flex items-center gap-2 ml-1">
                <HiInformationCircle className="text-blue-500" /> Description
              </label>
              <textarea
                name="description"
                placeholder="Tell clients what this covers..."
                rows="4"
                value={formData.description}
                onChange={handleChange}
                required
                className="input-field py-3 resize-none"
              />
            </div>

            {!isCategoryMode ? (
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 flex items-center gap-2 ml-1">
                  <HiSelector className="text-blue-500" /> Parent Category <span className="text-red-500">*</span>
                </label>
                <select
                  name="parent_id"
                  value={formData.parent_id}
                  onChange={handleChange}
                  required
                  className="input-field appearance-none cursor-pointer"
                >
                  <option value="">-- Select Parent Category --</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="p-3.5 bg-blue-50/60 border border-blue-100 rounded-2xl flex items-center gap-3">
                <HiCollection className="text-blue-600 text-lg shrink-0" />
                <div>
                  <p className="text-xs font-bold text-blue-900">Top-Level Category Mode</p>
                  <p className="text-[10px] text-blue-700/70 font-medium">Categories are top-level items and do not require a parent.</p>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Image Upload */}
          <div className="space-y-6">
            <div className="flex justify-between items-center ml-1">
              <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                <HiPhotograph className="text-blue-500" /> {isCategoryMode ? "Category Cover Images" : "Service Images (Multi-upload)"}
              </label>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isCategoryMode ? "text-slate-500 bg-slate-100" : "text-blue-600 bg-blue-50"}`}>
                {isCategoryMode ? "Optional" : "Auto-compressed < 300KB"}
              </span>
            </div>

              <div className="space-y-4">
                <label className="flex flex-col items-center justify-center w-full min-h-[160px] border-2 border-dashed border-slate-200 rounded-3xl bg-slate-50/50 hover:bg-white hover:border-blue-400 transition-all cursor-pointer group/inner shadow-inner p-6">
                  <div className="w-12 h-12 bg-white rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center mb-2 group-hover/inner:scale-110 group-hover/inner:text-blue-600 transition-all text-slate-400">
                    <HiUpload size={24} />
                  </div>
                  <span className="text-xs font-bold text-slate-600 uppercase tracking-widest text-center">
                    Click to attach service photos
                  </span>
                  <p className="text-[10px] text-slate-400 font-medium mt-1">
                    Upload up to 5 images • Auto-compressed
                  </p>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>

                {previews.length > 0 && (
                  <div className="grid grid-cols-3 gap-3">
                    {previews.map((src, idx) => (
                      <div
                        key={idx}
                        className="relative aspect-square rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-slate-100 group/item"
                      >
                        <img src={src} alt="Preview" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full text-xs shadow-md opacity-90 hover:opacity-100 transition-opacity"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest text-center px-4">
                Recommended aspect ratio: 1:1 or 4:3 for better catalog display.
              </p>
            </div>
          </div>

          <div className="pt-6">
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-4 rounded-2xl flex items-center justify-center gap-3 shadow-xl shadow-blue-500/20 group relative overflow-hidden active:scale-[0.98] transition-all disabled:opacity-70"
            >
              {isLoading ? (
                <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  <span className="font-bold tracking-wide">
                    Publish Service
                  </span>
                  <HiArrowRight
                    className="group-hover:translate-x-1.5 transition-transform duration-300"
                    size={20}
                  />
                </>
              )}
            </button>
          </div>
        </form>
    </div>
  );
}

export default CreateService;
